import { createCsrfRefresher, readCsrfToken } from '../api/csrf';

/**
 * Authorising a private channel with the Sanctum session cookie (P0-WEB-05).
 *
 * From the contract's transport notes: "Authorisation is the Sanctum session
 * cookie, not a token. The client must send credentials with the auth request;
 * there is nothing to put in an Authorization header."
 *
 * pusher-js's built-in authorizer sends a plain XHR with no credentials, so it
 * arrives at Laravel unauthenticated and every private subscription fails. This
 * replaces it via `channelAuthorization.customHandler`.
 *
 * `/v1/broadcasting/auth` sits behind the `api` middleware group
 * (`bootstrap/app.php`), which means `statefulApi()` — so the request needs the
 * CSRF header for the same reason every other write does, and a stale token
 * gets the same one-shot refresh and replay.
 *
 * It is also not in `docs/api/openapi.json`, because Laravel's broadcasting
 * route is registered by the framework rather than declared by the application.
 * That makes this the second and last hand-written fetch in the portal, after
 * the CSRF cookie itself.
 */

const AUTH_PATH = '/v1/broadcasting/auth';

export interface ChannelAuthorizationParams {
  readonly socketId: string;
  readonly channelName: string;
}

/** What Laravel returns: `{"auth": "<key>:<signature>"}`. */
export interface ChannelAuthorizationData {
  readonly auth: string;
  readonly channel_data?: string;
}

export type ChannelAuthorizationCallback = (
  error: Error | null,
  data: ChannelAuthorizationData | null,
) => void;

export type ChannelAuthorizationHandler = (
  params: ChannelAuthorizationParams,
  callback: ChannelAuthorizationCallback,
) => void;

export interface AuthorizerOptions {
  /** Platform API origin, no trailing slash. */
  readonly baseUrl: string;
  readonly fetch?: typeof globalThis.fetch;
}

export function createChannelAuthorizer({
  baseUrl,
  fetch: fetchImpl = globalThis.fetch.bind(globalThis),
}: AuthorizerOptions): ChannelAuthorizationHandler {
  const refreshCsrf = createCsrfRefresher(baseUrl, fetchImpl);

  async function authorize(params: ChannelAuthorizationParams): Promise<ChannelAuthorizationData> {
    let response = await post(params);

    if (response.status === 419) {
      await refreshCsrf();
      response = await post(params);
    }

    if (!response.ok) {
      // 403 here is the interesting one: the session is valid but the account
      // is not a member of that organisation. Naming the channel makes that
      // legible in a log, where "auth failed" alone never is.
      throw new Error(
        `Channel authorisation for ${params.channelName} failed with ${response.status}.`,
      );
    }

    const body: unknown = await response.json();

    if (
      typeof body !== 'object' ||
      body === null ||
      typeof (body as { auth?: unknown }).auth !== 'string'
    ) {
      throw new Error(`Channel authorisation for ${params.channelName} returned no signature.`);
    }

    return body as ChannelAuthorizationData;
  }

  function post({ socketId, channelName }: ChannelAuthorizationParams): Promise<Response> {
    const headers: Record<string, string> = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      // Laravel answers 419/422 as JSON rather than a redirect with this set.
      'X-Requested-With': 'XMLHttpRequest',
    };

    const token = readCsrfToken();
    if (token !== undefined) headers['X-XSRF-TOKEN'] = token;

    return fetchImpl(baseUrl + AUTH_PATH, {
      method: 'POST',
      headers,
      // The session cookie is the entire credential.
      credentials: 'include',
      body: JSON.stringify({ socket_id: socketId, channel_name: channelName }),
    });
  }

  return (params, callback) => {
    // pusher-js wants a callback, so the promise is adapted here rather than
    // leaked: an unhandled rejection inside the socket library surfaces as a
    // console error with no channel name and no retry.
    authorize(params).then(
      (data) => {
        callback(null, data);
      },
      (error: unknown) => {
        callback(error instanceof Error ? error : new Error(String(error)), null);
      },
    );
  };
}
